import React, { useState, useRef, useEffect, JSX } from 'react';
import { Box, Link, Typography, Avatar, SxProps, Tooltip, IconButton } from '@mui/material';
import { Divider, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMediaQuery } from '@mui/material';
import { Candidate, CandidateAI } from 'types/types';
import { CopyBubble } from 'components/CopyBubble';
import { rest } from 'lodash';
import { AIBanner } from 'components/ui/AIBanner';
import { useAuth } from 'hooks/AuthContext';

interface CandidateInfoProps {
  candidate: Candidate;
  sx?: SxProps;
  action?: string;
  elevation?: number;
  variant?: 'minimal' | 'small' | 'normal' | undefined;
}

interface ExpandingTextProps {
  content: string;
  lines?: number;
  sx?: SxProps;
}
const ExpandingText = (props: ExpandingTextProps): JSX.Element => {
  const { content, sx, lines = 3 } = props;
  const theme = useTheme();
  const [isTextExpanded, setIsTextExpanded] = useState<boolean>(false);
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState<boolean>(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current && content) {
      const element = textRef.current;
      // Check if the scrollHeight is greater than clientHeight (meaning content is truncated)
      setShouldShowMoreButton(element.scrollHeight > element.clientHeight);
    }
  }, [content]);

  return (
    <Box sx={{ minHeight: `${lines}rem`, ...sx }}>
      <Typography
        ref={textRef}
        variant="body1"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: isTextExpanded ? 'unset' : lines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.5,
          fontSize: '0.8rem !important',
        }}
      >
        {content}
      </Typography>
      {shouldShowMoreButton && (
        <Link
          component="button"
          variant="body2"
          onClick={(e): void => {
            e.preventDefault();
            e.stopPropagation();
            setIsTextExpanded(!isTextExpanded);
          }}
          sx={{
            color: theme.palette.primary.main,
            textDecoration: 'none',
            cursor: 'pointer',
            fontSize: '0.725rem',
            fontWeight: 500,
            mt: 0.5,
            display: 'block',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          [{isTextExpanded ? 'less' : 'more'}]
        </Link>
      )}
    </Box>
  );
};

const renderContactDetails = (candidate: Candidate): JSX.Element => (
  <>
    <Divider sx={{ my: 2 }} />
    {candidate.location && (
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Location:</strong>{' '}
        {candidate.location.city
          ? `${candidate.location.city}, ${candidate.location.state}`
          : candidate.location.text}
      </Typography>
    )}
    {candidate.email && (
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Email:</strong> {candidate.email}
      </Typography>
    )}
    {candidate.phone && (
      <Typography variant="body2">
        <strong>Phone:</strong> {candidate.phone}
      </Typography>
    )}
  </>
);

interface CandidateNameBoxProps {
  candidate: Candidate;
  action: string;
  isMobile: boolean;
  ai: CandidateAI | null;
}

const renderCandidateNameBox = ({
  candidate,
  action,
  isMobile,
  ai,
}: CandidateNameBoxProps): JSX.Element => (
  <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'left',
        gap: 1,
        minWidth: 0,
        '& > .MuiTypography-root': { m: 0 },
      }}
    >
      {action !== '' && <Typography variant="body1">{action}</Typography>}
      {action === '' && (
        <Typography
          variant="h5"
          component="h1"
          title={candidate.fullName}
          sx={{
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            pr: ai ? 4 : 0,
          }}
        >
          {candidate.fullName}
        </Typography>
      )}
    </Box>
    <Box sx={{ fontSize: '0.75rem', alignItems: 'center' }}>
      <Link href={`/u/${candidate.username}`}>{`/u/${candidate.username}`}</Link>
      <CopyBubble
        onClick={(event): void => {
          event.stopPropagation();
        }}
        tooltip="Copy link"
        content={`${window.location.origin}/u/{candidate.username}`}
      />
    </Box>
  </Box>
);

const renderAdminActions = (
  candidate: Candidate,
  deleteCandidate: (id: string | undefined) => Promise<void>
): JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
    }}
  >
    <Tooltip title="Delete Job">
      <IconButton
        size="small"
        onClick={(e): void => {
          e.stopPropagation();
          deleteCandidate(candidate.id);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

const CandidateInfo: React.FC<CandidateInfoProps> = (props: CandidateInfoProps) => {
  const { candidate } = props;
  const { user, apiClient } = useAuth();
  const { sx, action = '', variant = 'normal' } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || variant === 'minimal';
  const ai: CandidateAI | null = 'isAI' in candidate ? (candidate as CandidateAI) : null;
  const isAdmin = user?.isAdmin;

  const deleteCandidate = async (candidateId: string | undefined): Promise<void> => {
    if (candidateId) {
      await apiClient.deleteCandidate(candidateId);
    }
  };

  if (!candidate) {
    return <Box>No user loaded.</Box>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        transition: 'all 0.3s ease',
        flexGrow: 1,
        p: isMobile ? 1 : 2,
        height: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      {...rest}
    >
      {ai && <AIBanner variant={variant} />}

      <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, minWidth: 0 }}>
        <Avatar
          src={candidate.profileImage ? `/api/1.0/candidates/profile/${candidate.username}` : ''}
          alt={`${candidate.fullName}'s profile`}
          sx={{
            alignSelf: 'flex-start',
            width: isMobile ? 40 : 80,
            height: isMobile ? 40 : 80,
            border: '2px solid #e0e0e0',
          }}
        />

        <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1,
            }}
          >
            {renderCandidateNameBox({ candidate, action, isMobile, ai })}
          </Box>
        </Box>
      </Box>

      {variant === 'normal' && (
        <ExpandingText
          sx={{ display: 'flex', flexShrink: 1 }}
          content={candidate.description || ''}
        />
      )}
      <Box>{variant !== 'small' && variant !== 'minimal' && renderContactDetails(candidate)}</Box>
      {isAdmin && ai && renderAdminActions(candidate, deleteCandidate)}
    </Box>
  );
};

export { CandidateInfo };
